import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError, track, getUtm } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import * as Icons from "lucide-react";

const DRAFT_KEY = "ic_bc_draft";
function Icon({ name, className }) { const C = Icons[name] || Icons.Circle; return <C className={className} />; }

const STEPS = [
  { id: "vendite", title: "Vendite", fields: [
    { k: "revenue_annual", l: "Fatturato annuo stimato (€)", d: 250000 },
    { k: "monthly_transactions", l: "Scontrini/ordini medi al mese", d: 800 },
    { k: "avg_ticket", l: "Scontrino/ordine medio (€)", d: 25 },
    { k: "online_sales_pct", l: "% vendite online (0-100)", d: 0 },
    { k: "recurring_revenue_pct", l: "% ricavi ricorrenti (0-100)", d: 20 },
  ]},
  { id: "costi", title: "Costi", fields: [
    { k: "cost_of_goods_pct", l: "Costo merce / food cost (%)", d: 30 },
    { k: "rent_monthly", l: "Affitto mensile (€)", d: 1800 },
    { k: "personnel_monthly", l: "Costo personale mensile (€)", d: 4500 },
    { k: "other_fixed_monthly", l: "Altre spese fisse mensili (€)", d: 1200 },
  ]},
  { id: "organizzazione", title: "Organizzazione", fields: [
    { k: "employees", l: "Dipendenti / collaboratori", d: 3 },
    { k: "owner_hours_weekly", l: "Ore settimanali del titolare", d: 50 },
    { k: "years_active", l: "Anni di attività", d: 5 },
  ]},
  { id: "finanza", title: "Finanza (facoltativo)", fields: [
    { k: "liquidity_months", l: "Mesi di liquidità disponibile", d: 2 },
    { k: "has_debts", l: "Debiti finanziari in corso?", type: "select", options: ["No", "Sì, sostenibili", "Sì, pesanti"], d: "No" },
    { k: "main_goal", l: "Obiettivo principale", type: "select", options: ["Aumentare margini", "Crescita fatturato", "Liquidità/cassa", "Assunzioni", "Aprire nuova sede"], d: "Aumentare margini" },
  ]},
];

const PROFILE_FIELDS = [
  { k: "legal_name", l: "Ragione sociale" },
  { k: "trade_name", l: "Nome commerciale" },
  { k: "vat_number", l: "Partita IVA" },
  { k: "tax_code", l: "Codice fiscale impresa" },
  { k: "legal_form", l: "Forma giuridica", type: "select", options: ["Ditta individuale", "SNC", "SAS", "SRL", "SRLS", "SPA", "Cooperativa", "Altro"] },
  { k: "ateco_primary", l: "Codice ATECO principale" },
  { k: "ateco_secondary", l: "Codice ATECO secondario (facoltativo)" },
  { k: "foundation_year", l: "Anno di costituzione", type: "number" },
  { k: "city", l: "Comune" },
  { k: "province", l: "Provincia (sigla)" },
  { k: "region", l: "Regione" },
  { k: "revenue_band", l: "Fascia fatturato", type: "select", options: ["< €50k", "€50k - €150k", "€150k - €400k", "€400k - €1M", "> €1M"] },
];

export default function BusinessCheck() {
  const { user, register } = useAuth();
  const [params] = useSearchParams();
  const [sectors, setSectors] = useState([]);
  const [sel, setSel] = useState(params.get("sector") || "");
  const [form, setForm] = useState({});
  const [profile, setProfile] = useState({});
  const [reg, setReg] = useState({ name: "", email: "", password: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);

  // step flow stabile: 0 settore → 1..4 questionario → registrazione → profilo → risultato
  const flow = ["settore", ...STEPS.map(s => s.id), "registrazione", "profilo", "risultato"];
  const current = flow[stepIdx] || "settore";

  useEffect(() => {
    if (current === "registrazione" && user) setStepIdx(i => i + 1);
  }, [current, user]);

  useEffect(() => {
    api.get("/sectors").then(r => setSectors(r.data)).catch(() => {});
    try {
      const d = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      if (d) { setSel(s => s || d.sel || ""); setForm(d.form || {}); setProfile(d.profile || {}); }
    } catch {}
  }, []);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({ sel, form, profile }));
  }, [sel, form, profile]);

  const fieldVal = (f) => form[f.k] ?? f.d;
  const step = STEPS.find(s => s.id === current);

  const submitCheck = async () => {
    setLoading(true);
    // merge defaults per i campi non toccati
    const merged = { ...form };
    STEPS.forEach(s => s.fields.forEach(f => { if (merged[f.k] === undefined) merged[f.k] = f.d; }));
    try {
      if (user) {
        const p = { ...profile, business_type: sel, employees_count: merged.employees };
        await api.post("/companies", p);
        track("company_profile_completed");
      }
      const { data } = await api.post("/business-check/submit", { sector_slug: sel, ...merged });
      // Genera anche l'assessment Financial & Industry Engine (silenzioso)
      if (user) { api.post("/engine/assessment", { sector_slug: sel, ...merged }).catch(() => {}); }
      setResult(data);
      track("check_completed", { sector: sel });
      track("score_viewed", { score: data.score });
      track("improvement_value_viewed", { value: data.improvement_potential });
      localStorage.removeItem(DRAFT_KEY);
      setStepIdx(flow.length - 1);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  const doRegister = async () => {
    setLoading(true);
    try {
      await register(reg.email, reg.password, reg.name);
      toast.success("Account creato!");
      setStepIdx(stepIdx + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex gap-1.5 flex-1" data-testid="bc-progress">
          {flow.map((f, i) => (
            <div key={f} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= stepIdx ? 'bg-[#5424D5]' : 'bg-[#E5E4EB]'}`} />
          ))}
        </div>
        <span className="text-xs text-[#7A7788] font-semibold whitespace-nowrap">Passo {Math.min(stepIdx + 1, flow.length)} di {flow.length}</span>
      </div>
      <p className="text-xs text-[#7A7788] mb-8">Bozza salvata automaticamente — puoi riprendere più tardi.</p>

      {current === "settore" && (
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Business Check</h1>
          <p className="mt-2 text-[#524F5E]">Seleziona il tuo settore per iniziare</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8">
            {sectors.map(s => (
              <button key={s.slug} onClick={() => { setSel(s.slug); track("business_type_selected", { sector: s.slug }); track("check_started", { sector: s.slug }); setStepIdx(1); }}
                className="ic-card p-5 text-left" data-testid={`bc-sector-${s.slug}`}>
                <Icon name={s.icon} className="w-6 h-6 text-[#5424D5] mb-3" />
                <p className="font-semibold">{s.name}</p>
                <p className="text-xs text-[#7A7788]">{s.category}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {step && (
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">{step.title}</h1>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            {step.fields.map(f => (
              <label key={f.k} className="block">
                <span className="text-sm font-semibold text-[#171522]">{f.l}</span>
                {f.type === "select" ? (
                  <select value={fieldVal(f)} onChange={e => setForm({ ...form, [f.k]: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E5E4EB] bg-white focus:border-[#5424D5] focus:outline-none" data-testid={`bc-input-${f.k}`}>
                    {f.options.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type="number" value={fieldVal(f)} onChange={e => setForm({ ...form, [f.k]: Number(e.target.value) })}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E5E4EB] bg-white focus:border-[#5424D5] focus:outline-none" data-testid={`bc-input-${f.k}`} />
                )}
              </label>
            ))}
          </div>
          <div className="mt-8 flex gap-3">
            <button onClick={() => setStepIdx(stepIdx - 1)} className="ic-btn-ghost" data-testid="bc-back">Indietro</button>
            <button onClick={() => setStepIdx(stepIdx + 1)} className="ic-btn-primary" data-testid="bc-next">Continua</button>
          </div>
        </div>
      )}

      {current === "registrazione" && (
        <div className="max-w-md mx-auto">
          <h1 className="text-3xl font-extrabold tracking-tight">Crea il tuo account gratuito</h1>
          <p className="mt-2 text-[#524F5E]">Serve per salvare il tuo Business Check. Nessuna carta richiesta.</p>
          <div className="space-y-3 mt-6">
            <input placeholder="Nome" value={reg.name} onChange={e => setReg({ ...reg, name: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E5E4EB]" data-testid="bc-reg-name" />
            <input type="email" placeholder="Email" value={reg.email} onChange={e => setReg({ ...reg, email: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E5E4EB]" data-testid="bc-reg-email" />
            <input type="password" placeholder="Password (min. 6 caratteri)" value={reg.password} onChange={e => setReg({ ...reg, password: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-[#E5E4EB]" data-testid="bc-reg-password" />
            <button onClick={doRegister} disabled={loading || !reg.email || reg.password.length < 6} className="w-full ic-btn-primary" data-testid="bc-reg-submit">{loading ? "Creazione…" : "Crea account e continua"}</button>
            <p className="text-center text-sm text-[#524F5E]">Hai già un account? <Link to="/login" className="text-[#5424D5] font-semibold" data-testid="bc-login-link">Accedi</Link></p>
          </div>
        </div>
      )}

      {current === "profilo" && (
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Profilo azienda</h1>
          <p className="mt-2 text-[#524F5E]">Questi dati restano privati: visibili solo a te. Puoi anche completarli dopo.</p>
          <div className="grid md:grid-cols-2 gap-5 mt-8">
            {PROFILE_FIELDS.map(f => (
              <label key={f.k} className="block">
                <span className="text-sm font-semibold text-[#171522]">{f.l}</span>
                {f.type === "select" ? (
                  <select value={profile[f.k] || ""} onChange={e => setProfile({ ...profile, [f.k]: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E5E4EB] bg-white" data-testid={`profile-${f.k}`}>
                    <option value="">Seleziona…</option>
                    {f.options.map(o => <option key={o}>{o}</option>)}
                  </select>
                ) : (
                  <input type={f.type || "text"} value={profile[f.k] || ""} onChange={e => setProfile({ ...profile, [f.k]: e.target.value })}
                    className="mt-2 w-full px-4 py-3 rounded-xl border border-[#E5E4EB] bg-white" data-testid={`profile-${f.k}`} />
                )}
              </label>
            ))}
          </div>
          <p className="text-xs text-[#7A7788] mt-4">P.IVA e Codice Fiscale sono trattati come dati riservati: non compaiono mai in report aggregati e puoi richiederne la cancellazione in qualsiasi momento.</p>
          <div className="mt-6 flex gap-3">
            <button onClick={() => setStepIdx(stepIdx - 1)} className="ic-btn-ghost">Indietro</button>
            <button onClick={submitCheck} disabled={loading} className="ic-btn-primary" data-testid="bc-submit">{loading ? "Analisi…" : "Genera il mio Business Score"}</button>
          </div>
        </div>
      )}

      {current === "risultato" && result && (
        <div>
          <p className="text-xs text-[#7A7788] mb-3">I KPI finanziari P0 deterministici sono disponibili nel Report completo dopo il calcolo server-side.</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Il tuo ImpresaChiara Score</h1>
          <div className="grid md:grid-cols-2 gap-6 mt-8">
            <div className="ic-card p-8 text-center">
              <p className="text-xs uppercase tracking-widest text-[#7A7788]">Score complessivo</p>
              <p className="text-7xl font-extrabold text-[#5424D5] my-4" data-testid="bc-score">{result.score}<span className="text-2xl text-[#7A7788]">/100</span></p>
              <div className="h-3 bg-[#F4F4F7] rounded-full overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#5424D5] to-[#FF6A24]" style={{ width: `${result.score}%` }} />
              </div>
            </div>
            <div className="ic-card p-8">
              <p className="text-xs uppercase tracking-widest text-[#7A7788]">Potenziale di miglioramento individuato</p>
              <p className="text-4xl font-extrabold text-[#FF6A24] mt-3" data-testid="bc-improvement">€{result.improvement_potential?.toLocaleString('it-IT')} / anno</p>
              <p className="text-xs text-[#7A7788] mt-3 leading-relaxed">{result.improvement_disclaimer}</p>
            </div>
          </div>
          <div className="ic-card p-8 mt-6">
            <p className="text-xs uppercase tracking-widest text-[#7A7788] mb-4">Insight prioritari</p>
            {result.insights.map((ins, i) => (
              <div key={i} className="flex gap-3 mb-4" data-testid={`bc-insight-${i}`}>
                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-[#EDE8FC] text-[#5424D5]">
                  <Icons.Lightbulb className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{ins.title}</p>
                  <p className="text-sm text-[#524F5E]">{ins.text}</p>
                </div>
              </div>
            ))}
            {result.locked_insights?.map((ins, i) => (
              <div key={`l${i}`} className="flex gap-3 mb-4 opacity-70" data-testid={`bc-locked-${i}`}>
                <div className="w-8 h-8 rounded-lg flex-shrink-0 flex items-center justify-center bg-[#F4F4F7] text-[#7A7788]">
                  <Icons.Lock className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-semibold text-sm">{ins.title}</p>
                  <p className="text-sm text-[#7A7788]">Disponibile nei piani a pagamento</p>
                </div>
              </div>
            ))}
          </div>
          {result.plan === "free" && (
            <div className="mt-8 p-8 ic-card bg-[#171522] text-white flex flex-wrap items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold">Sblocca l'analisi completa</h3>
                <p className="text-white/70">3 insight completi, benchmark sintetico e storico da €4,90/mese + IVA.</p>
              </div>
              <a href="/pricing" className="ic-btn-accent" data-testid="bc-unlock-cta">Sblocca da €4,90/mese</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
